import { Checkbox, CheckboxProps, createStyles, MantineSize, Stack } from "@mantine/core";
import React from "react";
import { Minus } from "tabler-icons-react";
import { IconV2 } from "../..";
import { RpiAlert } from "../RpiAlert";

const checboxSizes: Record<MantineSize, number> = {
  xs: 16,
  sm: 18,
  md: 20,
  lg: 22,
  xl: 24,
};

const radioSizes: Record<MantineSize, number> = {
  xs: 8,
  sm: 10,
  md: 12,
  lg: 14,
  xl: 16,
};

type RpiCheckboxVariant = "default" | "radio";

interface RpiCheckboxProps {
  variant?: RpiCheckboxVariant;
  colors?: Record<"primary" | "icon" | "disabledPrimary" | "disabledLabel", string>;
}

const useRpiCheckboxStyles = createStyles<
  "root" | "body" | "inner" | "input",
  { size: MantineSize; variant: RpiCheckboxVariant; colors?: RpiCheckboxProps["colors"] }
>((theme, { size: checboxSize, variant, colors: defaultColors }) => {
  const colors: NonNullable<RpiCheckboxProps["colors"]> = defaultColors || {
    primary: theme.colors.brand[5],
    icon: theme.colors.sky[5],
    disabledPrimary: theme.colors.brand[3],
    disabledLabel: theme.colors.brand[4],
  };

  switch (variant) {
    case "default": {
      const size = checboxSizes[checboxSize];
      return {
        root: {},
        body: {
          alignItems: "flex-start",
          color: colors.primary,
        },
        inner: {
          width: size,
          height: size,
        },
        input: {
          width: size,
          height: size,
          cursor: "pointer",
          backgroundColor: "transparent",
          borderColor: colors.primary,
          borderWidth: 1,
          "&:checked": {
            backgroundColor: colors.primary,
            borderColor: colors.primary,
            "& + svg": {
              color: `${colors.icon} !important`,
            },
            "&:disabled": {
              backgroundColor: colors.disabledPrimary,
              borderColor: colors.disabledPrimary,
            },
          },
          "&:disabled": {
            backgroundColor: "transparent",
            borderColor: colors.disabledPrimary,
            "& + svg": {
              color: `${colors.disabledPrimary} !important`,
            },
          },
        },
        icon: {
          width: (size * 2) / 3,
          height: (size * 2) / 3,
          color: colors.primary,
        },
        label: {
          ...theme.other.fontTypes.label,
          fontWeight: theme.other.fontWeights.regular,
          color: colors.primary,
          "&[data-disabled]": {
            color: `${colors.disabledLabel} !important`,
          },
        },
      };
    }

    case "radio": {
      const size = radioSizes[checboxSize];
      return {
        root: {
          paddingLeft: 5,
        },
        body: {
          alignItems: "center",
          color: colors.primary,
        },
        inner: {
          width: size,
          height: size,
        },
        input: {
          width: size,
          height: size,
          cursor: "pointer",
          backgroundColor: "transparent",
          borderColor: colors.primary,
          borderRadius: "50%",
          border: "none",
          outline: `1px solid ${colors.primary} !important`,
          outlineOffset: "4px !important",
          "&:checked": {
            backgroundColor: colors.primary,
            outline: `1px solid ${colors.primary} !important`,
            outlineOffset: "4px !important",
            border: "none",
            "&:disabled": {
              backgroundColor: colors.disabledPrimary,
              outline: `1px solid ${colors.disabledPrimary} !important`,
              outlineOffset: "4px !important",
              border: "none",
            },
          },
          "&:disabled": {
            backgroundColor: "transparent",
            outline: `1px solid ${colors.disabledPrimary} !important`,
            outlineOffset: "4px !important",
            border: "none",
          },
          "&:active, &:focus": {
            outline: `1px solid ${colors.disabledPrimary} !important`,
            outlineOffset: "4px !important",
            border: "none",
          },
        },
        icon: {
          width: (size * 2) / 3,
          height: (size * 2) / 3,
          color: colors.icon,
        },
        label: {
          ...theme.other.fontTypes.label,
          fontWeight: theme.other.fontWeights.regular,
          color: colors.primary,
          paddingLeft: 17,
          "&[data-disabled]": {
            color: `${colors.disabledLabel} !important`,
          },
        },
      };
    }
  }
});

export const RpiCheckbox = React.forwardRef<HTMLInputElement, Omit<CheckboxProps, "classNames"> & RpiCheckboxProps>(
  ({ variant = "default", colors, ...props }, ref) => {
    const { classes } = useRpiCheckboxStyles({ size: props.size || "md", variant, colors });

    const CheckboxIcon: CheckboxProps["icon"] = ({ indeterminate, ...props }) => {
      switch (variant) {
        case "default":
          return indeterminate ? (
            <Minus color="currentColor" {...props} />
          ) : (
            <IconV2.Checkbox color="currentColor" {...props} />
          );
        default:
          return <></>;
      }
    };

    return <Checkbox ref={ref} classNames={classes} icon={CheckboxIcon} radius={4} {...props} />;
  }
);

const useRpiCheckboxV2Styles = createStyles((theme) => ({
  root: {
    "--line-height": "22px",
    "--font-weight": theme.other.fontWeights.normal,
    "&:has(input:checked)": {
      "--font-weight": theme.other.fontWeights.bold,
    },
  },
  body: {
    gap: theme.spacing.sm,
  },
  inner: {
    width: "unset",
    height: "var(--line-height)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  label: {
    fontSize: theme.fontSizes.md,
    lineHeight: "var(--line-height)",
    fontWeight: "var(--font-weight)" as any, // Intentionally break fontWeight type saftey
    padding: "unset",
  },
  input: {
    width: 18,
    height: 18,
    "&,&:checked": {
      border: `2px solid ${theme.other.semanticColors.icon.bold}`,
    },
    "&:checked": {
      background: theme.other.semanticColors.icon.bold,
    },
    "&:focus": {
      outline: `2px solid ${theme.other.semanticColors.borderFocus}`,
    },
  },
}));

export function RpiCheckboxV2({ error, ...props }: CheckboxProps) {
  const { classes } = useRpiCheckboxV2Styles();

  return (
    <Stack sx={(theme) => ({ gap: theme.spacing.sm })}>
      <Checkbox classNames={classes} {...props} />
      {error && <RpiAlert>{error}</RpiAlert>}
    </Stack>
  );
}
